import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  RefreshCw,
  ShieldOff,
  Terminal,
  X,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClientsService } from '../../services/api-clients.service';
import getConfig from '../../config/environments';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TableSkeleton,
} from '../ui';

/* ── helpers ──────────────────────────────────────────────────────────────── */

const { gatewayURL } = getConfig().api;

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateFull = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const truncate = (str, n = 24) =>
  str && str.length > n ? `${str.slice(0, n)}…` : str;

/* ── CopyButton ───────────────────────────────────────────────────────────── */
const CopyButton = ({ value, size = 'sm', className = '' }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const handleCopy = useCallback(
    (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      });
    },
    [value],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors
        ${copied
          ? 'bg-success-50 text-success-700 border border-success-200'
          : 'bg-ink-100 text-ink-500 hover:bg-ink-200 hover:text-ink-700 border border-transparent'
        } ${className}`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/* ── SecretBanner ─────────────────────────────────────────────────────────── */
const SecretBanner = ({ clientId, secret, onDismiss }) => {
  const [revealed, setRevealed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const timerRef = useRef({});

  const copy = useCallback((value, key, setter) => {
    navigator.clipboard.writeText(value).then(() => {
      setter(true);
      clearTimeout(timerRef.current[key]);
      timerRef.current[key] = setTimeout(() => setter(false), 2000);
    });
  }, []);

  return (
    <div className="rounded-xl border-2 border-warning-300 bg-warning-50 overflow-hidden">
      {/* Header strip */}
      <div className="px-5 py-3 bg-warning-100 border-b border-warning-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="h-4 w-4 text-warning-700 shrink-0" />
          <p className="text-sm font-semibold text-warning-900">
            Save your client secret — it won't be shown again
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded text-warning-600 hover:text-warning-900 hover:bg-warning-200 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-5 py-4 space-y-4">
        <p className="text-xs text-warning-800">
          Copy and store the credentials below in a secure vault. The secret is hashed immediately
          after creation and cannot be recovered.
        </p>

        {/* Client ID row */}
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-warning-700 mb-1.5">
            Client ID
          </p>
          <div className="flex items-center gap-2 bg-white rounded-lg border border-warning-200 px-3 py-2">
            <code className="flex-1 text-xs font-mono text-ink-800 select-all break-all">
              {clientId}
            </code>
            <button
              type="button"
              onClick={() => copy(clientId, 'id', setCopiedId)}
              className={`shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors border
                ${copiedId
                  ? 'bg-success-50 text-success-700 border-success-200'
                  : 'bg-ink-50 text-ink-600 border-ink-200 hover:bg-ink-100'
                }`}
            >
              {copiedId ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copiedId ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Client Secret row */}
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-warning-700 mb-1.5">
            Client Secret
          </p>
          <div className="flex items-center gap-2 bg-white rounded-lg border border-warning-200 px-3 py-2">
            <code className="flex-1 text-xs font-mono text-ink-800 select-all break-all">
              {revealed ? secret : '•'.repeat(Math.min(secret.length, 48))}
            </code>
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="shrink-0 p-1.5 rounded text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-colors"
              aria-label={revealed ? 'Hide secret' : 'Reveal secret'}
            >
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => copy(secret, 'secret', setCopiedSecret)}
              className={`shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors border
                ${copiedSecret
                  ? 'bg-success-50 text-success-700 border-success-200'
                  : 'bg-ink-50 text-ink-600 border-ink-200 hover:bg-ink-100'
                }`}
            >
              {copiedSecret ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copiedSecret ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Token snippet */}
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-warning-700 mb-1.5 flex items-center gap-1.5">
            <Terminal className="h-3 w-3" /> Quick start — generate token
          </p>
          <div className="bg-ink-900 rounded-lg px-4 py-3 relative group">
            <pre className="text-xs font-mono text-ink-200 whitespace-pre-wrap break-all leading-relaxed">
{`curl -X POST ${gatewayURL}/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_id": "${clientId}",
    "client_secret": "YOUR_SECRET"
  }'`}
            </pre>
            <CopyButton
              value={`curl -X POST ${gatewayURL}/v1/auth/token \\\n  -H "Content-Type: application/json" \\\n  -d '{"client_id":"${clientId}","client_secret":"${secret}"}'`}
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-ink-700 text-ink-200 border-ink-600 hover:bg-ink-600"
            />
          </div>
        </div>

        {/* Confirmation checkbox */}
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <div className="relative mt-0.5">
            <input
              type="checkbox"
              className="sr-only"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <div className={`h-4 w-4 rounded border-2 flex items-center justify-center transition-colors
              ${confirmed ? 'bg-warning-700 border-warning-700' : 'border-warning-400 bg-white'}`}
            >
              {confirmed && <Check className="h-2.5 w-2.5 text-white" />}
            </div>
          </div>
          <span className="text-xs text-warning-800">
            I have copied and securely stored the client secret. I understand it cannot be retrieved later.
          </span>
        </label>

        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={!confirmed}
            onClick={onDismiss}
            iconLeft={confirmed ? CheckCircle2 : undefined}
            className={confirmed ? 'bg-warning-700 border-warning-700 hover:bg-warning-800' : ''}
          >
            Done, I've saved the secret
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ── ClientStatusBadge ────────────────────────────────────────────────────── */
const ClientStatusBadge = ({ status }) => {
  const map = {
    ACTIVE: { tone: 'success', label: 'Active' },
    REVOKED: { tone: 'danger', label: 'Revoked' },
    INACTIVE: { tone: 'neutral', label: 'Inactive' },
  };
  const cfg = map[status] || { tone: 'neutral', label: status };
  return <Badge tone={cfg.tone} dot>{cfg.label}</Badge>;
};

/* ── Main component ───────────────────────────────────────────────────────── */
const ApiClients = () => {
  const { user } = useAuth();
  const myRole = user?.profile?.org_role;
  const canManage = myRole === 'OWNER' || myRole === 'ADMIN';

  /* list state */
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* new secret banners keyed by clientId */
  const [secrets, setSecrets] = useState({}); // { clientId: secret }

  /* create modal */
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  /* rotate */
  const [rotatingId, setRotatingId] = useState(null);
  const [rotatePending, setRotatePending] = useState(null); // clientId to confirm

  /* revoke */
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  /* load */
  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await apiClientsService.list();
      setClients(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load API clients. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* create */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const data = await apiClientsService.create({ name: createName.trim() });
      setClients((prev) => [data, ...prev]);
      setSecrets((prev) => ({ ...prev, [data.clientId]: data.clientSecret }));
      setCreateOpen(false);
      setCreateName('');
    } catch {
      setCreateError('Failed to create client. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  /* rotate */
  const handleRotateConfirm = async () => {
    if (!rotatePending) return;
    setRotatingId(rotatePending);
    setRotatePending(null);
    try {
      const data = await apiClientsService.rotateSecret(rotatePending);
      setSecrets((prev) => ({ ...prev, [rotatePending]: data.clientSecret }));
    } catch {
      setError('Failed to rotate secret. Please try again.');
    } finally {
      setRotatingId(null);
    }
  };

  /* revoke */
  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await apiClientsService.revoke(revokeTarget.clientId);
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === revokeTarget.clientId ? { ...c, status: 'REVOKED' } : c,
        ),
      );
      setRevokeTarget(null);
    } catch {
      setError('Failed to revoke client. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const activeCount = clients.filter((c) => c.status === 'ACTIVE').length;

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="API Clients"
        description="Machine-to-machine credentials for integrating your systems with the Tabadulat API. Each client generates a short-lived access token using the OAuth 2.0 client credentials flow."
        actions={
          canManage && (
            <Button
              variant="primary"
              iconLeft={Plus}
              onClick={() => setCreateOpen(true)}
            >
              New client
            </Button>
          )
        }
      />

      {/* Stats row */}
      {!loading && clients.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total clients', value: clients.length },
            { label: 'Active', value: activeCount },
            { label: 'Revoked', value: clients.length - activeCount },
          ].map(({ label, value }) => (
            <div key={label} className="card">
              <div className="card-body py-3">
                <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{label}</p>
                <p className="mt-1 text-2xl font-semibold tnum text-ink-900">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && <Alert tone="danger">{error}</Alert>}

      {/* Secret banners */}
      {Object.entries(secrets).map(([clientId, secret]) => (
        <SecretBanner
          key={clientId}
          clientId={clientId}
          secret={secret}
          onDismiss={() => setSecrets((prev) => { const n = { ...prev }; delete n[clientId]; return n; })}
        />
      ))}

      {/* How it works — info strip */}
      {!loading && clients.length === 0 && !error && (
        <div className="rounded-xl border border-info-100 bg-info-50 px-5 py-4 flex gap-4">
          <Zap className="h-5 w-5 text-info-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-info-900">How it works</p>
            <div className="text-xs text-info-700 space-y-1">
              <div className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Create a client to get a <strong>client_id</strong> and <strong>client_secret</strong>.</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Exchange them for a <strong>Bearer token</strong> via <code className="bg-info-100 px-1 rounded">POST /v1/auth/token</code>.</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Use the token in <code className="bg-info-100 px-1 rounded">Authorization: Bearer …</code> on every API call. Tokens expire in 1 hour.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table / empty */}
      {loading ? (
        <TableSkeleton rows={4} cols={5} />
      ) : clients.length === 0 ? (
        <Card pad={false}>
          <EmptyState
            icon={KeyRound}
            title="No API clients yet"
            description={
              canManage
                ? 'Create your first client to start integrating with the Tabadulat API.'
                : 'No API clients have been created for this organisation.'
            }
            action={
              canManage && (
                <Button variant="primary" iconLeft={Plus} onClick={() => setCreateOpen(true)}>
                  Create first client
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Card pad={false}>
          <CardHeader
            title="Credentials"
            description={`${activeCount} active client${activeCount !== 1 ? 's' : ''}`}
          />
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Client ID</TH>
                <TH>Status</TH>
                <TH>Last used</TH>
                <TH>Created</TH>
                {canManage && <TH />}
              </TR>
            </THead>
            <TBody>
              {clients.map((client) => (
                <TR key={client.id}>
                  <TD>
                    <div className="flex items-center gap-2.5">
                      <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0
                        ${client.status === 'ACTIVE' ? 'bg-accent-50' : 'bg-ink-100'}`}
                      >
                        <KeyRound className={`h-3.5 w-3.5 ${client.status === 'ACTIVE' ? 'text-accent-600' : 'text-ink-400'}`} />
                      </div>
                      <span className="text-sm font-medium text-ink-900">{client.name}</span>
                    </div>
                  </TD>
                  <TD>
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs font-mono text-ink-600 bg-ink-50 px-2 py-0.5 rounded border border-ink-100">
                        {truncate(client.clientId, 20)}
                      </code>
                      <CopyButton value={client.clientId} />
                    </div>
                  </TD>
                  <TD>
                    <ClientStatusBadge status={client.status} />
                  </TD>
                  <TD>
                    <span className="text-xs text-ink-500" title={fmtDateFull(client.lastUsedAt)}>
                      {client.lastUsedAt ? fmtDate(client.lastUsedAt) : 'Never'}
                    </span>
                  </TD>
                  <TD>
                    <span className="text-xs text-ink-500" title={fmtDateFull(client.createdAt)}>
                      {fmtDate(client.createdAt)}
                    </span>
                  </TD>
                  {canManage && (
                    <TD className="text-right">
                      {client.status === 'ACTIVE' && (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={RefreshCw}
                            loading={rotatingId === client.clientId}
                            onClick={() => setRotatePending(client.clientId)}
                            title="Rotate secret"
                          >
                            Rotate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={ShieldOff}
                            onClick={() => setRevokeTarget(client)}
                            title="Revoke client"
                            className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                          >
                            Revoke
                          </Button>
                        </div>
                      )}
                      {client.status === 'REVOKED' && (
                        <span className="text-xs text-ink-400">Revoked</span>
                      )}
                    </TD>
                  )}
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {/* ── Create modal ──────────────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        title="Create API client"
        description="A client ID and secret will be generated. Store the secret securely — it won't be recoverable."
        onClose={() => { setCreateOpen(false); setCreateError(null); setCreateName(''); }}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {createError && <Alert tone="danger">{createError}</Alert>}

          <Field
            label="Client name"
            required
            hint="A descriptive name to identify where this client is used, e.g. 'Production backend' or 'Data pipeline'."
          >
            <Input
              placeholder="e.g. Production backend"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              autoFocus
            />
          </Field>

          <div className="rounded-lg border border-warning-100 bg-warning-50 px-4 py-3 flex gap-2.5">
            <AlertTriangle className="h-4 w-4 text-warning-600 shrink-0 mt-0.5" />
            <p className="text-xs text-warning-800">
              The client secret is shown <strong>once</strong> immediately after creation. It is hashed and cannot be retrieved later. Store it in a secrets manager or vault.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setCreateOpen(false); setCreateError(null); setCreateName(''); }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={creating}
              disabled={!createName.trim()}
            >
              Create client
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Rotate confirm modal ──────────────────────────────────────────── */}
      <Modal
        open={!!rotatePending}
        title="Rotate client secret"
        onClose={() => setRotatePending(null)}
        width="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-600">
            A new secret will be generated and the existing one will be invalidated immediately. Any running service still using the old secret will receive{' '}
            <code className="text-xs bg-ink-100 px-1 py-0.5 rounded">401 Unauthorized</code> errors until updated.
          </p>
          <Alert tone="warning">
            Update your services with the new secret before rotating in production.
          </Alert>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRotatePending(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              iconLeft={RefreshCw}
              onClick={handleRotateConfirm}
            >
              Rotate secret
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Revoke confirm modal ──────────────────────────────────────────── */}
      <Modal
        open={!!revokeTarget}
        title="Revoke API client"
        onClose={() => setRevokeTarget(null)}
        width="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-600">
            Revoking <strong className="text-ink-900">{revokeTarget?.name}</strong> will permanently disable it. All active tokens issued for this client will be rejected immediately.
          </p>
          <Alert tone="danger" title="This action is permanent">
            A revoked client cannot be re-activated. Create a new client if you need to restore access.
          </Alert>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              iconLeft={ShieldOff}
              loading={revoking}
              onClick={handleRevoke}
            >
              Revoke client
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApiClients;
